import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MailProcessing, MailProcessingEvaluation } from '../db/MailExtractionResult.js';
import { MailProcessingFilter } from '../db/MailProcessingFilter.js';
import { MailProcessingStats } from '../db/MailProcessingStats.js';
import { resolveStatsSenderAddress, senderLookupStages } from '../server/stats-sender-pipeline.js';

export const getMailProcessingStats = registerQuery({
  type: () => MailProcessingStats,
  operationName: 'getMailProcessingStats',
  resolve: (filter?: MailProcessingFilter) => {
    return async (gqlFields: BaseQLSelectionSet<MailProcessingStats>) => {
      const mailProcessingCollection = await getRawCollection<MailProcessing>(MailProcessing._collectionName);

      // Optional sender scope (me-mode / a selected user). Joins the mail to read
      // the sender address; only added when scoping is requested so the common
      // unscoped case stays a plain match+group.
      const senderAddress = await resolveStatsSenderAddress(filter);
      const senderStages = senderAddress
        ? [...senderLookupStages(), { $match: { _senderAddress: senderAddress } }]
        : [];

      const processingStats = await mailProcessingCollection
        .aggregate<
          Pick<
            MailProcessingStats,
            | 'sumExtractions'
            | 'sumCorrect'
            | 'sumCorrectAfterCorrection'
            | 'sumCorrectWithAdjustment'
            | 'sumIncorrect'
            | 'sumUnsolvable'
            | 'sumNotEvaluated'
          >
        >([
          ...senderStages,
          {
            $match: filter?.getMongoMatchFilter() ?? {},
          },
          {
            $group: {
              _id: null,
              sumExtractions: { $sum: 1 },
              sumCorrect: {
                $sum: { $cond: [{ $eq: ['$evaluation', MailProcessingEvaluation.CORRECT] }, 1, 0] },
              },
              sumIncorrect: {
                $sum: { $cond: [{ $eq: ['$evaluation', MailProcessingEvaluation.FALSE] }, 1, 0] },
              },
              sumNotEvaluated: {
                $sum: { $cond: [{ $eq: ['$evaluation', null] }, 1, 0] },
              },
              sumCorrectAfterCorrection: {
                $sum: {
                  $cond: [{ $eq: ['$evaluation', MailProcessingEvaluation.CORRECT_AFTER_CORRECTION] }, 1, 0],
                },
              },
              sumCorrectWithAdjustment: {
                $sum: {
                  $cond: [{ $eq: ['$evaluation', MailProcessingEvaluation.CORRECT_WITH_ADJUSTMENT] }, 1, 0],
                },
              },
              sumUnsolvable: {
                $sum: { $cond: [{ $eq: ['$evaluation', MailProcessingEvaluation.UNSOLVABLE_INQUIRY] }, 1, 0] },
              },
            },
          },
        ])
        .toArray()
        .then(res => res[0]);

      return new MailProcessingStats({
        sumExtractions: processingStats?.sumExtractions ?? 0,
        sumCorrect: processingStats?.sumCorrect ?? 0,
        sumIncorrect: processingStats?.sumIncorrect ?? 0,
        sumNotEvaluated: processingStats?.sumNotEvaluated ?? 0,
        sumCorrectAfterCorrection: processingStats?.sumCorrectAfterCorrection ?? 0,
        sumCorrectWithAdjustment: processingStats?.sumCorrectWithAdjustment ?? 0,
        sumUnsolvable: processingStats?.sumUnsolvable ?? 0,
      });
    };
  },
  params: [
    {
      type: () => MailProcessingFilter,
      name: 'filter',
    },
  ],
});
